// import { LightGraphic } from "./LightGraphic"
import { GPUAPIObject } from './GPUAPIObject'
import * as GL from './webgl2constants'

type ArrayBufferConcreteView = Int8Array | Int16Array | Int32Array | Uint8Array | Uint16Array | Uint32Array | Uint8ClampedArray | Float32Array | Float64Array | DataView

function array2gltype(a: ArrayBufferConcreteView | ArrayBuffer | ArrayBufferView ): GLenum {
	if (a instanceof Int8Array) { return GL.BYTE }
	if (a instanceof Int16Array) { return GL.SHORT }
	if (a instanceof Int32Array) { /* console.log("WARNING : unsupported for attrib"); */ return GL.INT } // not listed ???
	// if (a instanceof BigInt64Array) { return GL.INT64 } // mentionned in https://developer.mozilla.org/en-US/docs/Web/API/WebGL_API/Types

	if (a instanceof Uint8Array) { return GL.UNSIGNED_BYTE }
	if (a instanceof Uint8ClampedArray) { return GL.UNSIGNED_BYTE } // TODO 
	if (a instanceof Uint16Array) { return GL.UNSIGNED_SHORT }
	if (a instanceof Uint32Array) { /* console.log("WARNING : unsupported for attrib"); */ return GL.UNSIGNED_INT } // not listed ???
	// if (a instanceof BigUint64Array) { return }

	// if (a instanceof Float16Array ) { return GL.GL.HALF_FLOAT } // TODO : half float doesn't exist in js...
	if (a instanceof Float32Array) { return GL.FLOAT }
	// if (a instanceof Float64Array) { return GL.FLOAT } // missing GL type

	// TODO : we should be able to bypass this and force a custom type

	return GL.FLOAT // TODO error
}

enum UsageMode {
	static = "static",
	stream = "stream",
	dynamic = "dynamic",
}

enum UsageAccess {
	draw = "draw",
	read = "read",
	copy = "copy",
}

function glUsage(usageMode: UsageMode, usageAccess: UsageAccess): GLenum {
	switch (usageMode) {
		case UsageMode.static:
			switch (usageAccess) {
				case UsageAccess.draw: return GL.STATIC_DRAW
				case UsageAccess.read: return GL.STATIC_READ
				case UsageAccess.copy: return GL.STATIC_COPY
				default: throw `usageAccess must be "${Object.keys(UsageAccess).join(`" or "`)}" but is "${usageAccess}"`
			}
		case UsageMode.stream:
			switch (usageAccess) {
				case UsageAccess.draw: return GL.STREAM_DRAW
				case UsageAccess.read: return GL.STREAM_READ
				case UsageAccess.copy: return GL.STREAM_COPY
				default: throw `usageAccess must be "${Object.keys(UsageAccess).join(`" or "`)}" but is "${usageAccess}"`
			}
		case UsageMode.dynamic:
			switch (usageAccess) {
				case UsageAccess.draw: return GL.DYNAMIC_DRAW
				case UsageAccess.read: return GL.DYNAMIC_READ
				case UsageAccess.copy: return GL.DYNAMIC_COPY
				default: throw `usageAccess must be "${Object.keys(UsageAccess).join(`" or "`)}" but is "${usageAccess}"`
			}
		default: throw `usageMode must be "${Object.keys(UsageMode).join(`" or "`)}" but is "${usageMode}"`
	}
}

export abstract class GPUBuffer extends GPUAPIObject<WebGLBuffer> {
	// alias for this.obj
	get buffer() {return this.obj}
	set buffer(buf) {this.obj = buf}

	protected abstract get target(): GLenum
	protected abstract get targetBinding(): GLenum

	protected createFunction(): WebGLBuffer | null { return this.gl.createBuffer() }
	protected deleteFunction(): void { this.gl.deleteBuffer(this.buffer) }
	bind(): () => void {
		let gbefore = this.gl.getParameter(this.targetBinding)
		this.gl.bindBuffer(this.target, this.buffer)
		return () => this.gl.bindBuffer(this.target, gbefore)
	}

	constructor(gl: WebGL2RenderingContext) {
		super(gl)
		this.usageMode = UsageMode.static
		this.usageAccess = UsageAccess.draw
	}

	usageMode: UsageMode
	usageAccess: UsageAccess

	protected get usage(): GLenum { return glUsage(this.usageMode, this.usageAccess) }

	/**
	 * get : show allocated memory on GPU (in bytes) <br>
	 * set : reallocate memory on GPU (in bytes)
	 */
	get size() {
		let out: number = 0
		this.use(() => {
			out = this.gl.getBufferParameter(this.target,GL.BUFFER_SIZE)
		})
		return out
	}
	set size(sizeInByte: number) {
		this.use(() => {
			this.gl.bufferData(this.target, sizeInByte, this.usage)
		})
	}

	#type = GL.FLOAT
	get type() { return this.#type }

	get gpumem() {
		return this.get(0)
	}
	set gpumem(data: ArrayBufferConcreteView | ArrayBuffer | Array<number>) {
		if( data instanceof Array) {
			// default to float32
			// TODO : what about integers buffer ? does this fail ?
			data = new Float32Array(data)
		} 
		this.setData(data)
	}

	/** write in the buffer on the GPU */
	setData(src: ArrayBufferConcreteView | ArrayBuffer, srcOffset: number = 0, length?: number): this {
		this.#type = array2gltype(src)
		// why can't I use ArrayBufferView ? doesn't make sense
		this.use(() => {
			this.gl.bufferData(this.target, src, this.usage, srcOffset, length)
		})
		return this
	}
	/** write in the buffer on the GPU */
	setSubData(dstByteOffset: number, src: ArrayBufferView, srcOffset: number, length?: number): this {
		// this.#type = array2gltype(src) // TODO : check compatibility ? a warning ?
		// why can't I use ArrayBuffer ? doesn't make sense
		this.use(() => {
			this.gl.bufferSubData(this.target, dstByteOffset, src, srcOffset, length)
		})
		return this
	}
	/** write in the buffer on the GPU */
	set(src: ArrayBufferConcreteView, srcOffset: number = 0, length?: number, dstByteOffset?: number): this {
		this.#type = array2gltype(src)
		this.use(() => {
			if (!dstByteOffset) { /* ie 0 or undefined */
				this.gl.bufferData(this.target, src, this.usage, srcOffset, length)
			} else {
				this.gl.bufferSubData(this.target, dstByteOffset, src, srcOffset, length)
			}
		})
		return this
	}

	// TODO test :
	// - buffer too big
	/** read the buffer on the GPU */
	get(srcByteOffset: number = 0, dstBuffer?: ArrayBufferView, dstOffset?: number, length?: number): ArrayBuffer {
		// why can't I use ArrayBuffer ? doesn't make sense
		let buf: ArrayBufferView = dstBuffer ? dstBuffer : new DataView(new ArrayBuffer(length ? length : this.size - srcByteOffset))
		this.use(() => {
			this.gl.getBufferSubData(this.target, srcByteOffset, buf, dstOffset, length)
		})
		return buf.buffer
	}

	static copy(read: GPUBuffer, write: GPUBuffer, size: number, readOffset: number = 0, writeOffset: number = 0) {
		read.checkValidity()
		write.checkValidity()
		if (read.gl != write.gl ) throw "cannot mix object from different webgl contexts"
		let gl = read.gl
		// TODO : https://www.khronos.org/registry/webgl/specs/latest/2.0/#COPYING_BUFFERS
		//    canot copy ElementArrayBuffer to other buffers ie :
		//       - ElementArrayBuffer with ElementArrayBuffer
		//       - everything else with everything else but not ElementArrayBuffer
		let gbeforeR = gl.getParameter(GL.COPY_READ_BUFFER_BINDING)
		let gbeforeW = gl.getParameter(GL.COPY_WRITE_BUFFER_BINDING)
		gl.bindBuffer(GL.COPY_READ_BUFFER,read.buffer)
		gl.bindBuffer(GL.COPY_WRITE_BUFFER,write.buffer)
		gl.copyBufferSubData(GL.COPY_READ_BUFFER, GL.COPY_WRITE_BUFFER, readOffset, writeOffset, size)
		gl.bindBuffer(GL.COPY_READ_BUFFER, gbeforeR)
		gl.bindBuffer(GL.COPY_WRITE_BUFFER, gbeforeW)
	}
	copyFrom(read: GPUBuffer, size: number, readOffset: number, writeOffset: number): this {
		GPUBuffer.copy(read, this, size, readOffset, writeOffset)
		return this
	}
	copyTo(write: GPUBuffer, size: number, readOffset: number, writeOffset: number):this {
		GPUBuffer.copy(this, write, size, readOffset, writeOffset)
		return this
	}

}

export class GPUElementArrayBuffer      extends GPUBuffer { /** @internal */ get target() {return GL.ELEMENT_ARRAY_BUFFER      } /** @internal */ get targetBinding() { return GL.ELEMENT_ARRAY_BUFFER_BINDING      } constructor(gl: WebGL2RenderingContext) {super(gl);this.use() /* first activation needed */}}
export class GPUArrayBuffer             extends GPUBuffer { /** @internal */ get target() {return GL.ARRAY_BUFFER              } /** @internal */ get targetBinding() { return GL.ARRAY_BUFFER_BINDING              } constructor(gl: WebGL2RenderingContext) {super(gl);this.use() /* first activation needed */}}
// TODO : put them back when used and tested
// export class GPUPixelPackBuffer         extends GPUBuffer { /** @internal */ get target() {return GL.PIXEL_PACK_BUFFER         } /** @internal */ get targetBinding() { return GL.PIXEL_PACK_BUFFER_BINDING         } constructor(gl: WebGL2RenderingContext) {super(gl);this.use() /* first activation needed */}}
// export class GPUPixelUnpackBuffer       extends GPUBuffer { /** @internal */ get target() {return GL.PIXEL_UNPACK_BUFFER       } /** @internal */ get targetBinding() { return GL.PIXEL_UNPACK_BUFFER_BINDING       } constructor(gl: WebGL2RenderingContext) {super(gl);this.use() /* first activation needed */}}
// export class GPUTransformFeedbackBuffer extends GPUBuffer { /** @internal */ get target() {return GL.TRANSFORM_FEEDBACK_BUFFER } /** @internal */ get targetBinding() { return GL.TRANSFORM_FEEDBACK_BUFFER_BINDING } constructor(gl: WebGL2RenderingContext) {super(gl);this.use() /* first activation needed */}}
// export class GPUUniformBuffer           extends GPUBuffer { /** @internal */ get target() {return GL.UNIFORM_BUFFER            } /** @internal */ get targetBinding() { return GL.UNIFORM_BUFFER_BINDING            } constructor(gl: WebGL2RenderingContext) {super(gl);this.use() /* first activation needed */}}
